import React from 'react';
import { MdErrorOutline, MdGppGood, MdRefresh } from 'react-icons/md';

export const CATEGORY_LABELS = {
    abbreviation: 'Undefined abbreviation',
    ambiguous: 'Ambiguous',
    vague: 'Vague',
    missing_context: 'Missing context',
    contradiction: 'Contradiction',
    incomplete: 'Incomplete',
    noise: 'Extraction noise',
};

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'high', label: 'High' },
    { key: 'medium', label: 'Medium' },
    { key: 'low', label: 'Low' },
];

const issueKey = (issue) => `${issue.Position}-${issue.Category}-${issue.Quote}`;

// Where the reviewed text could make the assistant answer wrongly, each asked as a question for the author.
// The selected row opens to show why it matters, and its passage is highlighted in the text.
export default function AiHallucinationPanel({ review, onLocate }) {
    const { status, issues, details, error, retry } = review;
    const [filter, setFilter] = React.useState('all');
    const [selected, setSelected] = React.useState(null);
    const checking = status === 'checking';

    const counts = React.useMemo(() => issues.reduce((totals, issue) => {
        totals[issue.Severity] = (totals[issue.Severity] || 0) + 1;
        return totals;
    }, { all: issues.length }), [issues]);
    // A re-check can empty the chosen severity; fall back to all.
    const activeFilter = counts[filter] ? filter : 'all';
    const visible = activeFilter === 'all' ? issues : issues.filter((issue) => issue.Severity === activeFilter);

    const select = (issue) => {
        const key = issueKey(issue);
        setSelected((current) => (current === key ? null : key));
        onLocate?.(issue.Quote);
    };

    return (
        <aside className="hc-ai-halluc d-flex flex-column" aria-label="Hallucination risks" aria-busy={checking}>
            <div className="hc-ai-halluc-head d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <span className="fw-bold hc-text-primary text-truncate">Hallucination risks</span>
                    {checking ? (
                        <span className="spinner-border spinner-border-sm flex-shrink-0" role="status" aria-label="Checking" style={{ width: 13, height: 13 }} />
                    ) : null}
                </div>
                {status === 'done' || status === 'error' ? (
                    <button type="button" className="hc-ai-halluc-refresh border-0 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" title="Check again" aria-label="Check again" onClick={retry}>
                        <MdRefresh size={16} />
                    </button>
                ) : null}
            </div>

            {issues.length ? (
                <div className="hc-ai-halluc-filters d-flex gap-1" role="tablist" aria-label="Filter by severity">
                    {FILTERS.filter(({ key }) => key === 'all' || counts[key]).map(({ key, label }) => (
                        <button
                            type="button"
                            key={key}
                            role="tab"
                            aria-selected={activeFilter === key}
                            className={`hc-ai-halluc-filter is-${key} rounded-pill ${activeFilter === key ? 'is-active' : ''}`}
                            onClick={() => setFilter(key)}
                        >
                            {label} <span className="hc-ai-halluc-count">{counts[key]}</span>
                        </button>
                    ))}
                </div>
            ) : null}

            <div className={`hc-ai-halluc-body flex-grow-1 overflow-auto ${checking && issues.length ? 'is-stale' : ''}`} style={{ overscrollBehavior: 'contain' }}>
                {status === 'idle' ? (
                    <div className="hc-ai-halluc-empty">The text is checked as soon as extraction finishes.</div>
                ) : null}
                {checking && !issues.length ? (
                    <div className="hc-ai-halluc-empty">Looking for passages that could mislead the assistant...</div>
                ) : null}
                {status === 'error' ? (
                    <div className="hc-ai-halluc-empty hc-ai-src-error d-flex align-items-start gap-1" role="alert">
                        <MdErrorOutline size={15} className="flex-shrink-0 mt-1" />
                        <span>{error}. You can still submit.</span>
                    </div>
                ) : null}
                {status === 'done' && !issues.length ? (
                    <div className="hc-ai-halluc-empty d-flex flex-column align-items-center text-center gap-2">
                        <MdGppGood size={30} className="hc-ai-src-ok" />
                        <span>Nothing in this text looks likely to mislead the assistant.</span>
                    </div>
                ) : null}

                {visible.map((issue) => {
                    const key = issueKey(issue);
                    const open = selected === key;
                    return (
                        <button
                            type="button"
                            key={key}
                            className={`hc-ai-halluc-issue is-${issue.Severity} ${open ? 'is-open' : ''} w-100 text-start`}
                            onClick={() => select(issue)}
                            aria-expanded={open}
                        >
                            <span className="hc-ai-halluc-cat d-flex align-items-center gap-2">
                                <span className="hc-ai-halluc-dot rounded-circle flex-shrink-0" aria-label={`${issue.Severity} risk`} />
                                {CATEGORY_LABELS[issue.Category] || issue.Category}
                            </span>
                            {issue.Question ? <span className="hc-ai-halluc-question d-block">{issue.Question}</span> : null}
                            <span className="hc-ai-halluc-quote d-block">&ldquo;{issue.Quote}&rdquo;</span>
                            {open && issue.Explanation ? <span className="hc-ai-halluc-detail d-block">{issue.Explanation}</span> : null}
                        </button>
                    );
                })}
            </div>

            {details?.Truncated || details?.FailedSections ? (
                <div className="hc-ai-halluc-foot">
                    {details.Truncated ? `Checked the first ${details.CheckedCharacters.toLocaleString()} of ${details.TotalCharacters.toLocaleString()} characters.` : ''}
                    {details.FailedSections ? ` ${details.FailedSections} section(s) could not be checked.` : ''}
                </div>
            ) : null}
        </aside>
    );
}
